import * as JSZipModule from "jszip";
import type {
  PreslinkCreateDataShape,
  PreslinkCreatedShape,
} from "./preslinkCreateDataApi";

const EMU_PER_POINT = 12_700;
const BDM_NAME_PATTERN = /^_bdm\.[0-9a-f]+\.edm$/i;

interface ZipEntry {
  async(type: "string"): Promise<string>;
}

interface MutableZip {
  file(path: string): ZipEntry | null;
  file(path: string, contents: string, options?: { base64: boolean }): unknown;
  generateAsync(options: {
    type: "base64";
    compression: "DEFLATE";
  }): Promise<string>;
}

interface MinimalJSZip {
  loadAsync(data: string, options: { base64: boolean }): Promise<MutableZip>;
}

const JSZip = ((JSZipModule as unknown as { default?: MinimalJSZip }).default ??
  (JSZipModule as unknown as MinimalJSZip));

export interface PreslinkWorkbookGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PreslinkWorkbookTarget {
  name: string;
  sheetName: string;
  kind: "chart" | "group";
  geometry?: PreslinkWorkbookGeometry;
}

export interface PreslinkTagMainFields {
  fileName: string;
  path: string;
  landmark: string;
  address: string;
  sheetSlideName: string;
}

export interface PreslinkOutputPlacement extends PreslinkWorkbookGeometry {
  targetIndex: number;
}

function decodeXml(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function encodeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function readAttribute(xml: string, name: string): string {
  const match = new RegExp(`\\b${name}="([^"]*)"`, "i").exec(xml);
  return match ? decodeXml(match[1]) : "";
}

function resolvePartPath(basePart: string, target: string): string {
  if (target.startsWith("/")) return target.replace(/^\/+/, "");
  const baseSegments = basePart.split("/");
  baseSegments.pop();
  for (const segment of target.replace(/\\/g, "/").split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") baseSegments.pop();
    else baseSegments.push(segment);
  }
  return baseSegments.join("/");
}

async function readRequiredPart(zip: MutableZip, path: string): Promise<string> {
  const entry = zip.file(path);
  if (!entry) throw new Error(`Workbook is missing required OOXML part ${path}.`);
  return entry.async("string");
}

function relationshipMap(xml: string): Map<string, string> {
  const output = new Map<string, string>();
  for (const match of xml.matchAll(/<Relationship\b([^>]*)\/?>/gi)) {
    const attributes = match[1] ?? "";
    const id = readAttribute(attributes, "Id");
    const target = readAttribute(attributes, "Target");
    if (id && target) output.set(id, target);
  }
  return output;
}

function cNvPrName(xml: string): string {
  const match = /<(?:xdr:)?cNvPr\b([^>]*)\/?>/i.exec(xml);
  return match ? readAttribute(match[1] ?? "", "name") : "";
}

function xfrmGeometry(xml: string): PreslinkWorkbookGeometry | undefined {
  // Spreadsheet graphic frames carry `xdr:xfrm`; the backreference keeps the
  // closing prefix matched so a nested shape's `a:xfrm` cannot be spliced in.
  const xfrm = /<((?:xdr:|a:)?)xfrm\b[^>]*>([\s\S]*?)<\/\1xfrm>/i.exec(xml)?.[2];
  if (!xfrm) return undefined;
  const off = /<(?:a:)?off\b([^>]*)\/?>/i.exec(xfrm)?.[1] ?? "";
  const ext = /<(?:a:)?ext\b([^>]*)\/?>/i.exec(xfrm)?.[1] ?? "";
  const x = Number(readAttribute(off, "x"));
  const y = Number(readAttribute(off, "y"));
  const cx = Number(readAttribute(ext, "cx"));
  const cy = Number(readAttribute(ext, "cy"));
  if (![x, y, cx, cy].every(Number.isFinite) || cx <= 0 || cy <= 0) {
    return undefined;
  }
  return {
    left: x / EMU_PER_POINT,
    top: y / EMU_PER_POINT,
    width: cx / EMU_PER_POINT,
    height: cy / EMU_PER_POINT,
  };
}

function drawingTargets(xml: string, sheetName: string): PreslinkWorkbookTarget[] {
  const output: PreslinkWorkbookTarget[] = [];
  const anchors =
    xml.match(
      /<xdr:(?:twoCellAnchor|oneCellAnchor|absoluteAnchor)\b[\s\S]*?<\/xdr:(?:twoCellAnchor|oneCellAnchor|absoluteAnchor)>/gi,
    ) ?? [];
  for (const anchor of anchors) {
    const group = /<xdr:grpSp\b[\s\S]*?<\/xdr:grpSp>/i.exec(anchor)?.[0];
    if (group && /<c:chart\b/i.test(group)) {
      const name = cNvPrName(group);
      if (!name) throw new Error("Chart-containing Excel group has no object name.");
      output.push({ name, sheetName, kind: "group" });
      continue;
    }
    const frame = /<xdr:graphicFrame\b[\s\S]*?<\/xdr:graphicFrame>/i.exec(anchor)?.[0];
    if (!frame || !/<c:chart\b/i.test(frame)) continue;
    const name = cNvPrName(frame);
    const geometry = xfrmGeometry(frame);
    if (!name || !geometry) {
      throw new Error("Excel chart is missing its object name or xfrm geometry.");
    }
    output.push({ name, sheetName, kind: "chart", geometry });
  }
  return output;
}

export async function discoverPreslinkWorkbookTargets(
  workbookBase64: string,
): Promise<PreslinkWorkbookTarget[]> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const workbookXml = await readRequiredPart(zip, "xl/workbook.xml");
  const workbookRels = relationshipMap(
    await readRequiredPart(zip, "xl/_rels/workbook.xml.rels"),
  );
  const sheets = Array.from(
    workbookXml.matchAll(/<sheet\b([^>]*)\/?>/gi),
    (match) => ({
      name: readAttribute(match[1] ?? "", "name"),
      relationshipId: readAttribute(match[1] ?? "", "r:id"),
    }),
  ).filter((sheet) => sheet.name && sheet.relationshipId);

  const targets: PreslinkWorkbookTarget[] = [];
  for (const sheet of sheets) {
    const worksheetTarget = workbookRels.get(sheet.relationshipId);
    if (!worksheetTarget) continue;
    const worksheetPath = resolvePartPath("xl/workbook.xml", worksheetTarget);
    const worksheetXml = await readRequiredPart(zip, worksheetPath);
    const drawingRelationshipId =
      /<drawing\b([^>]*)\/?>/i.exec(worksheetXml)?.[1] ?? "";
    const drawingRid = readAttribute(drawingRelationshipId, "r:id");
    if (!drawingRid) continue;
    const worksheetFileName = worksheetPath.split("/").pop() ?? "";
    const worksheetRelsPath = worksheetPath.replace(
      /\/([^/]+)$/,
      `/_rels/${worksheetFileName}.rels`,
    );
    const worksheetRelsEntry = zip.file(worksheetRelsPath);
    if (!worksheetRelsEntry) continue;
    const drawingTarget = relationshipMap(
      await worksheetRelsEntry.async("string"),
    ).get(drawingRid);
    if (!drawingTarget) continue;
    const drawingPath = resolvePartPath(worksheetPath, drawingTarget);
    targets.push(
      ...drawingTargets(await readRequiredPart(zip, drawingPath), sheet.name),
    );
  }

  if (targets.length === 1) return targets;
  if (
    targets.length === 2 &&
    targets.every((target) => target.kind === "chart") &&
    targets[0].sheetName === targets[1].sheetName &&
    new Set(targets.map((target) => target.name)).size === 2 &&
    targets.some((target) => target.name === "Chart 1") &&
    targets.some((target) => target.name === "Chart 2")
  ) {
    return targets.sort((left, right) => left.name.localeCompare(right.name));
  }
  throw new Error(
    `Unsupported ChartCreator workbook structure: found ${targets.length} linkable top-level objects.`,
  );
}

function relationshipList(xml: string): Array<{ type: string; target: string }> {
  return Array.from(xml.matchAll(/<Relationship\b([^>]*)\/?>/gi), (match) => ({
    type: readAttribute(match[1] ?? "", "Type"),
    target: readAttribute(match[1] ?? "", "Target"),
  })).filter((relationship) => relationship.type && relationship.target);
}

function partRelsPath(partPath: string): string {
  return partPath.replace(/\/([^/]+)$/, "/_rels/$1.rels");
}

async function worksheetPaths(zip: MutableZip): Promise<Map<string, string>> {
  const workbookXml = await readRequiredPart(zip, "xl/workbook.xml");
  const workbookRels = relationshipMap(
    await readRequiredPart(zip, "xl/_rels/workbook.xml.rels"),
  );
  const output = new Map<string, string>();
  for (const match of workbookXml.matchAll(/<sheet\b([^>]*)\/?>/gi)) {
    const sheetName = readAttribute(match[1] ?? "", "name");
    const worksheetTarget = workbookRels.get(readAttribute(match[1] ?? "", "r:id"));
    if (sheetName && worksheetTarget) {
      output.set(sheetName, resolvePartPath("xl/workbook.xml", worksheetTarget));
    }
  }
  return output;
}

async function sheetDrawings(
  zip: MutableZip,
): Promise<Array<{ sheetName: string; drawingPath: string }>> {
  const output: Array<{ sheetName: string; drawingPath: string }> = [];
  for (const [sheetName, worksheetPath] of await worksheetPaths(zip)) {
    const relsXml = await zip.file(partRelsPath(worksheetPath))?.async("string");
    for (const relationship of relationshipList(relsXml ?? "")) {
      if (/\/drawing$/i.test(relationship.type)) {
        output.push({
          sheetName,
          drawingPath: resolvePartPath(worksheetPath, relationship.target),
        });
      }
    }
  }
  return output;
}

export interface PreslinkLinkedPicture {
  /** Picture name in its sheet's drawing, e.g. "Picture 7". */
  name: string;
  sheetName: string;
  /** Preslink target for the picture's source range, e.g. `'Sheet'!AD5:AM14`. */
  rangeTarget: string;
  rangeSheet: string;
}

function linkedPictureRange(
  sheetName: string,
  cellRange: string,
): Pick<PreslinkLinkedPicture, "rangeTarget" | "rangeSheet"> | undefined {
  const match =
    /^(?:(?:'((?:[^']|'')+)'|([^'!]+))!)?(\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?)$/i.exec(
      cellRange.trim(),
    );
  if (!match) return undefined;
  const rangeSheet = match[1]?.replace(/''/g, "'") ?? match[2] ?? sheetName;
  return {
    rangeSheet,
    rangeTarget: `'${rangeSheet.replace(/'/g, "''")}'!${match[3].replace(/\$/g, "")}`,
  };
}

/**
 * Linked ("camera") pictures that show a plain cell range. Their stored
 * snapshot is only redrawn by Excel, and ChartCreator templates ship it
 * holding sample data, so other renderers show the wrong company.
 */
export async function findLinkedPictures(
  workbookBase64: string,
): Promise<PreslinkLinkedPicture[]> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const output: PreslinkLinkedPicture[] = [];
  for (const { sheetName, drawingPath } of await sheetDrawings(zip)) {
    const drawing = await zip.file(drawingPath)?.async("string");
    for (const picture of drawing?.match(/<xdr:pic\b[\s\S]*?<\/xdr:pic>/gi) ?? []) {
      const cellRange = readAttribute(
        /<a14:cameraTool\b([^>]*)\/?>/i.exec(picture)?.[1] ?? "",
        "cellRange",
      );
      const name = cNvPrName(picture);
      const range = cellRange ? linkedPictureRange(sheetName, cellRange) : undefined;
      if (name && range) output.push({ name, sheetName, ...range });
    }
  }
  return output;
}

const IMAGE_RELATIONSHIP_TYPE =
  "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image";
const EMPTY_RELATIONSHIPS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';

function appendRelationship(relsXml: string, relationship: string): string {
  const open = relsXml.replace(/<Relationships\b([^>]*?)\s*\/>/i, "<Relationships$1></Relationships>");
  return open.replace(/<\/Relationships>/i, `${relationship}</Relationships>`);
}

/**
 * Points each linked picture at a fresh PNG snapshot of its range. Excel still
 * redraws the picture from the live cells; other renderers draw the snapshot.
 */
export async function replaceLinkedPictureSnapshots(
  workbookBase64: string,
  snapshots: Array<Pick<PreslinkLinkedPicture, "name" | "sheetName"> & { image: string }>,
): Promise<string> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  let mediaIndex = 0;
  let replaced = 0;
  for (const { sheetName, drawingPath } of await sheetDrawings(zip)) {
    const sheetSnapshots = snapshots.filter((snapshot) => snapshot.sheetName === sheetName);
    const drawing = sheetSnapshots.length
      ? await zip.file(drawingPath)?.async("string")
      : undefined;
    if (!drawing) continue;
    const relsPath = partRelsPath(drawingPath);
    let rels = (await zip.file(relsPath)?.async("string")) ?? EMPTY_RELATIONSHIPS;
    const updated = drawing.replace(/<xdr:pic\b[\s\S]*?<\/xdr:pic>/gi, (picture) => {
      const snapshot = sheetSnapshots.find((entry) => entry.name === cNvPrName(picture));
      if (!snapshot || !/<a:blip\b[^>]*\br:embed="/i.test(picture)) return picture;
      let mediaName: string;
      do {
        mediaIndex += 1;
        mediaName = `preslinkSnapshot${mediaIndex}.png`;
      } while (zip.file(`xl/media/${mediaName}`));
      let relationshipId: string;
      let idIndex = 0;
      do {
        idIndex += 1;
        relationshipId = `rIdPreslinkSnapshot${idIndex}`;
      } while (rels.includes(`Id="${relationshipId}"`));
      zip.file(`xl/media/${mediaName}`, snapshot.image, { base64: true });
      rels = appendRelationship(
        rels,
        `<Relationship Id="${relationshipId}" Type="${IMAGE_RELATIONSHIP_TYPE}" Target="../media/${mediaName}"/>`,
      );
      replaced += 1;
      return picture.replace(/(<a:blip\b[^>]*\br:embed=")[^"]*(")/i, `$1${relationshipId}$2`);
    });
    if (updated === drawing) continue;
    zip.file(drawingPath, updated);
    zip.file(relsPath, rels);
  }
  if (replaced === 0) return workbookBase64;
  const contentTypes = await readRequiredPart(zip, "[Content_Types].xml");
  if (!/<Default\b[^>]*\bExtension="png"/i.test(contentTypes)) {
    zip.file(
      "[Content_Types].xml",
      contentTypes.replace(
        /(<Types\b[^>]*>)/i,
        '$1<Default Extension="png" ContentType="image/png"/>',
      ),
    );
  }
  return zip.generateAsync({ type: "base64", compression: "DEFLATE" });
}

function withAttribute(tag: string, name: string, value: string): string {
  const existing = new RegExp(`\\b${name}="[^"]*"`);
  return existing.test(tag)
    ? tag.replace(existing, `${name}="${value}"`)
    : tag.replace(/^(<\w+)/, `$1 ${name}="${value}"`);
}

/** No color, automatic, the theme's dark text, or a near-black gray. */
function isDarkTextFont(font: string): boolean {
  const color = /<color\b([^>]*?)\/?>/.exec(font)?.[1];
  if (color === undefined || /\bauto="(?:1|true)"/.test(color)) return true;
  if (readAttribute(color, "theme")) return readAttribute(color, "theme") === "1";
  if (readAttribute(color, "indexed")) return ["8", "64"].includes(readAttribute(color, "indexed"));
  const rgb = /^(?:[0-9a-f]{2})?([0-9a-f]{6})$/i.exec(readAttribute(color, "rgb"))?.[1];
  if (!rgb) return false;
  const channels = [0, 2, 4].map((offset) => parseInt(rgb.slice(offset, offset + 2), 16));
  return Math.max(...channels) <= 0x60 && Math.max(...channels) - Math.min(...channels) <= 0x20;
}

function cellColumn(letters: string): number {
  let column = 0;
  for (const letter of letters.toUpperCase()) column = column * 26 + letter.charCodeAt(0) - 64;
  return column;
}

/**
 * Gives the dark text in linked pictures' source cells `color` (RRGGBB), so
 * Excel, which draws those pictures from the live cells, keeps them readable
 * over a dark chart. Only those cells get the cloned font and cell format.
 */
export async function recolorLinkedPictureText(
  workbookBase64: string,
  pictures: readonly Pick<PreslinkLinkedPicture, "rangeTarget" | "rangeSheet">[],
  color: string,
): Promise<string> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const styles = await readRequiredPart(zip, "xl/styles.xml");
  const fontsBlock = /(<fonts\b[^>]*>)([\s\S]*?)<\/fonts>/.exec(styles);
  const xfsBlock = /(<cellXfs\b[^>]*>)([\s\S]*?)<\/cellXfs>/.exec(styles);
  if (!fontsBlock || !xfsBlock) return workbookBase64;
  const fonts: string[] = fontsBlock[2].match(/<font\b[^>]*\/>|<font\b[^>]*>[\s\S]*?<\/font>/g) ?? [];
  const xfs: string[] = xfsBlock[2].match(/<xf\b[^>]*\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g) ?? [];
  const restyled = new Map<number, number>();
  const restyle = (style: number): number => {
    let next = restyled.get(style);
    if (next !== undefined) return next;
    next = style;
    const xf = xfs[style];
    const font = xf ? fonts[Number(readAttribute(xf, "fontId") || 0)] : undefined;
    if (xf && font && isDarkTextFont(font)) {
      const colorElement = `<color rgb="FF${color}"/>`;
      // CT_Font children may come in any order.
      fonts.push(
        /<color\b[^>]*\/>/.test(font)
          ? font.replace(/<color\b[^>]*\/>/, colorElement)
          : font.replace(/^<font\b([^>]*?)\s*(\/?)>/, (_open, attributes: string, empty: string) =>
              empty ? `<font${attributes}>${colorElement}</font>` : `<font${attributes}>${colorElement}`,
            ),
      );
      const opening = /^<xf\b[^>]*?(?=\/?>)/.exec(xf)?.[0] ?? "<xf";
      const reopened = withAttribute(withAttribute(opening, "fontId", String(fonts.length - 1)), "applyFont", "1");
      xfs.push(reopened + xf.slice(opening.length));
      next = xfs.length - 1;
    }
    restyled.set(style, next);
    return next;
  };

  const paths = await worksheetPaths(zip);
  let recoloredCells = 0;
  for (const sheetName of new Set(pictures.map((picture) => picture.rangeSheet))) {
    const path = paths.get(sheetName);
    const sheetXml = path ? await zip.file(path)?.async("string") : undefined;
    if (!path || !sheetXml) continue;
    const ranges = pictures
      .filter((picture) => picture.rangeSheet === sheetName)
      .flatMap((picture) => {
        const match = /!([A-Z]{1,3})(\d+)(?::([A-Z]{1,3})(\d+))?$/i.exec(picture.rangeTarget);
        if (!match) return [];
        return [{
          firstColumn: cellColumn(match[1]),
          firstRow: Number(match[2]),
          lastColumn: cellColumn(match[3] ?? match[1]),
          lastRow: Number(match[4] ?? match[2]),
        }];
      });
    const updated = sheetXml.replace(/<c\b([^>]*?)(\/?)>/g, (cell, attributes: string, empty: string) => {
      const address = /^([A-Z]{1,3})(\d+)$/i.exec(readAttribute(attributes, "r"));
      if (!address) return cell;
      const column = cellColumn(address[1]);
      const row = Number(address[2]);
      const inRange = ranges.some(
        (range) =>
          column >= range.firstColumn && column <= range.lastColumn && row >= range.firstRow && row <= range.lastRow,
      );
      if (!inRange) return cell;
      const style = Number(readAttribute(attributes, "s") || 0);
      const next = restyle(style);
      if (next === style) return cell;
      recoloredCells += 1;
      return `${withAttribute(`<c${attributes}`, "s", String(next))}${empty}>`;
    });
    if (updated !== sheetXml) zip.file(path, updated);
  }
  if (recoloredCells === 0) return workbookBase64;
  zip.file(
    "xl/styles.xml",
    styles
      .replace(fontsBlock[0], () => `${withAttribute(fontsBlock[1], "count", String(fonts.length))}${fonts.join("")}</fonts>`)
      .replace(xfsBlock[0], () => `${withAttribute(xfsBlock[1], "count", String(xfs.length))}${xfs.join("")}</cellXfs>`),
  );
  return zip.generateAsync({ type: "base64", compression: "DEFLATE" });
}

/**
 * A frame needing more than this is too thin to show a readable chart at any
 * size, and enlarging it to the designed height would ask create-data for an
 * image tens of thousands of points long.
 */
const MAX_RENDER_SCALE = 4;

/**
 * The frame's shape, enlarged to at least the chart's designed size. Drawn any
 * smaller, its fixed point-size text crowds out axis labels and legend entries;
 * the picture is still placed within the frame.
 */
function renderSize(
  frame: { width: number; height: number },
  design: PreslinkWorkbookGeometry | undefined,
): { width: number; height: number } {
  const scale = design
    ? Math.min(MAX_RENDER_SCALE, Math.max(1, design.width / frame.width, design.height / frame.height))
    : 1;
  return {
    width: Math.round(frame.width * scale * 100) / 100,
    height: Math.round(frame.height * scale * 100) / 100,
  };
}

export function buildCreateDataShapes(
  targets: PreslinkWorkbookTarget[],
  targetFile: string,
  frame: { width: number; height: number },
): PreslinkCreateDataShape[] {
  const isTargetsRatingsPair = targets.length === 2;
  return targets.map((target) => ({
    targetName: target.name,
    targetSheet: target.sheetName,
    targetFile,
    ...(!isTargetsRatingsPair ? { size: renderSize(frame, target.geometry) } : {}),
  }));
}

export function parsePreslinkTagMainFields(
  tags: Record<string, string>,
): PreslinkTagMainFields {
  const xml = tags.DMSOFTTAG;
  const main = typeof xml === "string" ? /<Main\b([^>]*)\/?>/i.exec(xml)?.[1] : "";
  if (!main) throw new Error("Preslink output is missing a valid DMSOFTTAG Main element.");
  const fields = {
    fileName: readAttribute(main, "FileName"),
    path: readAttribute(main, "Path"),
    landmark: readAttribute(main, "Landmark"),
    address: readAttribute(main, "Address"),
    sheetSlideName: readAttribute(main, "SheetSlideName"),
  };
  if (Object.values(fields).some((value) => !value)) {
    throw new Error("Preslink output tag is missing required source fields.");
  }
  return fields;
}

function splitTargetFile(targetFile: string): { path: string; fileName: string } {
  const lastSlash = Math.max(targetFile.lastIndexOf("\\"), targetFile.lastIndexOf("/"));
  if (lastSlash < 0) return { path: "", fileName: targetFile };
  return {
    path: targetFile.slice(0, lastSlash),
    fileName: targetFile.slice(lastSlash + 1),
  };
}

export function verifyPreslinkOutputs(
  requests: PreslinkCreateDataShape[],
  outputs: PreslinkCreatedShape[],
): PreslinkTagMainFields[] {
  if (requests.length !== outputs.length) {
    throw new Error("Preslink service output count does not match the requested targets.");
  }
  return outputs.map((output, index) => {
    const request = requests[index];
    const fields = parsePreslinkTagMainFields(output.tags);
    const target = splitTargetFile(request.targetFile);
    if (fields.path !== target.path || fields.fileName !== target.fileName) {
      throw new Error("Preslink output tag does not reference the requested workbook path.");
    }
    if (fields.landmark !== request.targetName) {
      throw new Error("Preslink output tag landmark does not match its requested Excel object.");
    }
    const expectedAddress = `${request.targetSheet}!${request.targetName}`;
    if (fields.address !== expectedAddress) {
      throw new Error("Preslink output tag address does not match its requested Excel object.");
    }
    if (BDM_NAME_PATTERN.test(fields.landmark)) {
      throw new Error("Range and text Preslink outputs are outside chart-creation scope.");
    }
    if (!BDM_NAME_PATTERN.test(fields.sheetSlideName)) {
      throw new Error("Preslink output tag has an invalid sheet landmark name.");
    }
    return fields;
  });
}

function sheetReference(sheetName: string): string {
  const escaped = sheetName.replace(/'/g, "''");
  return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(sheetName)
    ? `${sheetName}!$1:$1048576`
    : `'${escaped}'!$1:$1048576`;
}

export async function addMissingPreslinkSheetNames(
  workbookBase64: string,
  requests: PreslinkCreateDataShape[],
  outputs: PreslinkCreatedShape[],
): Promise<{ workbookBase64: string; addedNames: string[] }> {
  const fields = verifyPreslinkOutputs(requests, outputs);
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const workbookPath = "xl/workbook.xml";
  let workbookXml = await readRequiredPart(zip, workbookPath);
  const existing = new Map<string, string>();
  for (const match of workbookXml.matchAll(
    /<definedName\b([^>]*)>([\s\S]*?)<\/definedName>/gi,
  )) {
    const name = readAttribute(match[1] ?? "", "name");
    if (name) existing.set(name, decodeXml((match[2] ?? "").trim()));
  }
  const additions: Array<{ name: string; reference: string }> = [];
  fields.forEach((field, index) => {
    const reference = sheetReference(requests[index].targetSheet);
    const found = existing.get(field.sheetSlideName);
    if (found !== undefined && found !== reference) {
      throw new Error(
        `Workbook name ${field.sheetSlideName} already exists with a conflicting reference.`,
      );
    }
    if (found === undefined && !additions.some((entry) => entry.name === field.sheetSlideName)) {
      additions.push({ name: field.sheetSlideName, reference });
    }
  });
  if (additions.length === 0) {
    return { workbookBase64, addedNames: [] };
  }
  const xml = additions
    .map(
      ({ name, reference }) =>
        `<definedName name="${encodeXml(name)}" hidden="1">${encodeXml(reference)}</definedName>`,
    )
    .join("");
  if (/<definedNames\b/i.test(workbookXml)) {
    workbookXml = workbookXml.replace(/<\/definedNames>/i, `${xml}</definedNames>`);
  } else if (/<\/sheets>/i.test(workbookXml)) {
    workbookXml = workbookXml.replace(
      /<\/sheets>/i,
      `</sheets><definedNames>${xml}</definedNames>`,
    );
  } else {
    throw new Error("Workbook XML contains no sheets collection.");
  }
  zip.file(workbookPath, workbookXml);
  return {
    workbookBase64: await zip.generateAsync({
      type: "base64",
      compression: "DEFLATE",
    }),
    addedNames: additions.map((entry) => entry.name),
  };
}

/** Pixel size from a PNG's IHDR chunk, or undefined when the bytes lack one. */
function readPngSize(pngBase64: string): { width: number; height: number } | undefined {
  let header: string;
  try {
    header = atob(pngBase64.slice(0, 32));
  } catch {
    return undefined;
  }
  if (header.length < 24 || header.slice(12, 16) !== "IHDR") return undefined;
  const uint32 = (offset: number) =>
    ((header.charCodeAt(offset) << 24) |
      (header.charCodeAt(offset + 1) << 16) |
      (header.charCodeAt(offset + 2) << 8) |
      header.charCodeAt(offset + 3)) >>> 0;
  const width = uint32(16);
  const height = uint32(20);
  return width > 0 && height > 0 ? { width, height } : undefined;
}

/** The largest box with `size`'s proportions that fits inside `frame`, centred in it. */
function fitInside(
  size: { width: number; height: number },
  frame: PreslinkWorkbookGeometry,
): PreslinkWorkbookGeometry {
  const width = Math.min(frame.width, (frame.height * size.width) / size.height);
  const height = Math.min(frame.height, (frame.width * size.height) / size.width);
  return {
    left: frame.left + (frame.width - width) / 2,
    top: frame.top + (frame.height - height) / 2,
    width,
    height,
  };
}

/**
 * The frame that pictures laid out by `layoutPreslinkOutputs` were fitted into,
 * moved and scaled with any change the user has since made to them.
 */
export function frameAroundFittedPictures(
  pictures: PreslinkWorkbookGeometry,
  frame: PreslinkWorkbookGeometry,
): PreslinkWorkbookGeometry {
  const fitted = fitInside(pictures, frame);
  const scale = pictures.width / fitted.width;
  return {
    left: pictures.left - (fitted.left - frame.left) * scale,
    top: pictures.top - (fitted.top - frame.top) * scale,
    width: frame.width * scale,
    height: frame.height * scale,
  };
}

export function layoutPreslinkOutputs(
  targets: PreslinkWorkbookTarget[],
  frame: PreslinkWorkbookGeometry,
  outputs: PreslinkCreatedShape[],
): PreslinkOutputPlacement[] {
  if (targets.length === 1) {
    // create-data scales the size it is asked for by a display-scale
    // correction, so the picture need not have the frame's proportions.
    const size = readPngSize(outputs[0]?.image ?? "");
    return [{ targetIndex: 0, ...(size ? fitInside(size, frame) : frame) }];
  }
  const geometries = targets.map((target) => target.geometry);
  if (geometries.some((geometry) => !geometry)) {
    throw new Error("Multi-target Preslink layout requires xfrm geometry for every chart.");
  }
  const safe = geometries as PreslinkWorkbookGeometry[];
  const minLeft = Math.min(...safe.map((geometry) => geometry.left));
  const minTop = Math.min(...safe.map((geometry) => geometry.top));
  const maxRight = Math.max(...safe.map((geometry) => geometry.left + geometry.width));
  const maxBottom = Math.max(...safe.map((geometry) => geometry.top + geometry.height));
  const unionWidth = maxRight - minLeft;
  const unionHeight = maxBottom - minTop;
  if (unionWidth <= 0 || unionHeight <= 0) {
    throw new Error("Multi-target Preslink layout has invalid source geometry.");
  }
  const scale = Math.min(frame.width / unionWidth, frame.height / unionHeight);
  const occupiedWidth = unionWidth * scale;
  const occupiedHeight = unionHeight * scale;
  const originLeft = frame.left + (frame.width - occupiedWidth) / 2;
  const originTop = frame.top + (frame.height - occupiedHeight) / 2;
  return safe.map((geometry, targetIndex) => ({
    targetIndex,
    left: originLeft + (geometry.left - minLeft) * scale,
    top: originTop + (geometry.top - minTop) * scale,
    width: geometry.width * scale,
    height: geometry.height * scale,
  }));
}
